/**
 * zWork backend client.
 *
 * In `vite dev`, requests to `/api/*` are proxied to :8787 by Vite.
 * In a bundled Tauri app, the frontend is served from `tauri://`, so we must
 * rewrite `/api/*` to an absolute `http://127.0.0.1:8787/api/*`. The Tauri
 * Rust side launches the backend on that port at app startup.
 *
 * In web mode (app.tryzwork.app), Caddy proxies `/api/*` to the Axum cloud API.
 */
import { invoke } from "@tauri-apps/api/core";
import packageJson from "../../package.json";
import { isDemoMode } from "./preview";
import { isMacOS } from "./platform";

const IS_TAURI =
  typeof window !== "undefined" &&
  // Tauri v2 exposes this; keep broad checks for v1 fallback too.
  (!!(window as any).__TAURI_INTERNALS__ ||
    !!(window as any).__TAURI__ ||
    (window.location && window.location.protocol === "tauri:"));

/** True when running as a web app (not Tauri, not a local vite dev server on any port). */
export const IS_WEB =
  typeof window !== "undefined" &&
  !IS_TAURI &&
  !["localhost", "127.0.0.1"].includes(window.location.hostname);

const APP_VERSION = packageJson.version ?? "unknown";

function clientPlatform(): string {
  if (typeof window === "undefined") return "server";
  if (IS_TAURI) return "desktop";
  const nav = window.navigator as any;
  if (nav?.userAgentData?.platform) return nav.userAgentData.platform;
  return nav?.platform ?? "web";
}

function clientHeaders(): Record<string, string> {
  return {
    "x-zwork-app-version": APP_VERSION,
    "x-zwork-os": clientPlatform(),
  };
}

let sidecarTokenPromise: Promise<string> | null = null;

/** Per-run token authenticating requests to the local sidecar. Minted by the
 *  Tauri host at launch; the sidecar rejects requests without it. Cached after
 *  the first fetch. Resolves to "" outside Tauri (browser dev / web mode talk
 *  to other backends that don't require it). */
function getSidecarToken(): Promise<string> {
  if (!sidecarTokenPromise) {
    sidecarTokenPromise = IS_TAURI
      ? invoke<string>("get_sidecar_token").catch(() => "")
      : Promise.resolve("");
  }
  return sidecarTokenPromise;
}

// The web app calls api.tryzwork.app directly: the Better Auth session cookie
// is scoped to that host, so same-origin calls from app.tryzwork.app arrive
// without it and 401.
const API_BASE = IS_TAURI ? "http://127.0.0.1:8787" : IS_WEB ? "https://api.tryzwork.app" : "";

/** Sends the session cookie on the web app's cross-origin API calls. */
const WEB_CREDENTIALS: RequestInit = IS_WEB ? { credentials: "include" } : {};

/** One progress record from Ollama's /api/pull stream. */
export interface OllamaPullProgress {
  status: string; // "pulling manifest", "downloading", "verifying", "success", "error"
  digest?: string;
  total?: number;
  completed?: number;
  error?: string;
}

function u(path: string): string {
  // `path` always starts with "/api/..."
  return API_BASE + path;
}

export interface ApiMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: number;
  activities?: Array<{
    id: string;
    label: string;
    icon?: string;
    done: boolean;
  }>;
}

export interface ApiChat {
  id: string;
  title: string;
  created_at: number;
  updated_at: number;
  model: string;
  messages: ApiMessage[];
  /** True while a run is still writing this chat's reply. */
  running?: boolean;
}

export interface ApiChatSummary {
  id: string;
  title: string;
  created_at: number;
  updated_at: number;
  message_count: number;
  model: string;
  project_id?: string;
  /** Short plaintext snippet of the most recent message — powers content
   *  matching in SearchModal. Empty string when unavailable. */
  preview?: string;
  /** `"chat"` (interactive, default) or `"automation"` (scheduled-task run).
   *  Filter `automation` out of the main chat list — they surface inside the
   *  scheduled task's run history instead. */
  kind?: string;
}

export interface Integration {
  id: string;
  name: string;
  detected: boolean;
  can_reuse_credentials: boolean;
  detail: string;
  path: string;
}

export interface ComposioStatus {
  enabled: boolean;
  configured: boolean;
  available: boolean;
  connected_apps: string[];
  tool_count: number;
  user_id: string;
}

export interface ComposioAccount {
  app: string;
  status: string;
  account_id: string;
  app_name: string;
  icon: string;
  color: string;
}

export interface ComposioApp {
  id: string;
  name: string;
  icon: string | null;
  color: string;
}

export interface CredentialStatus {
  configured: boolean;
  source: "byok" | "claude_code" | "env" | null;
  base_url: string | null;
  shape: string;
  api?: string;
}

export interface ModelEntry {
  id: string;
  name: string;
  subtitle: string;
  shape: string;
  credential: string;
  model_id: string;
  base_url_override?: string;
  configured: boolean;
  synthesized: boolean;
}

export interface ProvidersResponse {
  credentials: Record<string, CredentialStatus>;
  models: ModelEntry[];
  default_model: string;
}

export type McpServerState = "connected" | "connecting" | "error" | "idle" | "disabled";

/** State of the managed runtime (`/api/runtime`). */
export type RuntimeStatus =
  | { state: "missing" }
  | { state: "installing"; step: string }
  | { state: "ready"; python: string; node: string }
  | { state: "failed"; error: string }
  | { state: "unsupported" };

/** An MCP connector as served by `/api/mcp/servers`. */
export interface McpServer {
  name: string;
  transport: "stdio" | "http" | "sse";
  /** Command line or URL. */
  target: string;
  enabled: boolean;
  state: McpServerState;
  error: string | null;
  server_name: string | null;
  server_version: string | null;
  tools: { name: string; description: string; read_only: boolean }[];
  config: Record<string, unknown>;
}

/** Servers configured in another app (Claude, Cursor, VS Code, …). */
export interface McpDiscoverySource {
  id: string;
  label: string;
  servers: { name: string; transport: string; target: string; already_added: boolean }[];
}

export interface McpAddResult {
  success?: boolean;
  error?: string;
  results?: { name: string; ok: boolean; error: string | null }[];
}

/** A models.dev provider as served by `/api/providers/catalog`. */
export interface CatalogProvider {
  id: string;
  name: string;
  supported: boolean;
  api: string | null;
  base_url: string;
  /** Env vars that hold the secret key, e.g. ["OPENAI_API_KEY"]. */
  env: string[];
  /** Non-secret URL placeholders, e.g. ["AZURE_RESOURCE_NAME"]. */
  vars: string[];
  doc: string;
  keyless: boolean;
  model_count: number;
  configured: boolean;
}

export interface CatalogModel {
  id: string;
  name: string;
  reasoning: boolean;
  images: boolean;
  context: number;
  output: number;
  cost: { input: number; output: number; cache_read: number; cache_write: number };
  api: string;
  base_url: string;
}

export interface CustomModel {
  id: string;
  name: string;
  shape: string;
  credential: string;
  model_id: string;
  base_url_override: string;
}

export interface SettingsPublic {
  default_model: string;
  use_claude_code_config: boolean;
  telemetry_enabled: boolean;
  api_keys: Record<string, string>;
  provider_config: Record<string, Record<string, string>>;
  custom_models: CustomModel[];
}

async function j<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`${r.status} ${r.statusText}: ${text}`);
  }
  return (await r.json()) as T;
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Map a model id the router echoes (a tier alias, or an upstream id from an
 * older router) to the user-facing tier id, so upstream providers are never
 * shown. Sol is ??????; every other id is the auto router.
 */
export function whitelabelModelName(upstreamId: string | null | undefined): string | undefined {
  if (!upstreamId) return undefined;
  const id = upstreamId.toLowerCase();
  if (id === "zwork-apex" || id.includes("gpt-6.1-sol")) return "zwork-apex";
  if (id.startsWith("zwork-") || /gemini|luna|glm|deepseek|mimo/.test(id)) return "zwork-auto";
  return undefined;
}

async function invokeBackendCommand(command: "ensure_backend" | "restart_backend") {
  if (!IS_TAURI) return;
  try {
    await invoke(command);
  } catch {
    // The HTTP health check below is authoritative; ignore invoke failures in
    // browser/dev contexts where the command may not exist yet.
  }
}

async function healthFetch() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5_000);
  const token = await getSidecarToken();
  return fetch(u("/api/health"), {
    signal: controller.signal,
    headers: token ? { "x-zwork-token": token } : undefined,
  })
    .then((r) => j<{ ok: boolean; version: string }>(r))
    .finally(() => clearTimeout(timeout));
}

async function waitForBackendReady(attempts = 60) {
  let lastError: unknown = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      if (i === 0 || (i > 0 && i % 15 === 0)) await invokeBackendCommand("ensure_backend");
      return await healthFetch();
    } catch (err) {
      lastError = err;
      await sleep(i < 10 ? 500 : 1500);
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error("Backend did not become ready.");
}

async function localFetch(path: string, init?: RequestInit) {
  if (IS_TAURI) {
    await waitForBackendReady(6);
  }
  const token = await getSidecarToken();
  if (token) {
    const headers = new Headers(init?.headers);
    headers.set("x-zwork-token", token);
    init = { ...init, headers };
  }
  return fetch(u(path), { ...WEB_CREDENTIALS, ...init });
}

export interface MeResponse {
  name: string;
  os: string;
  cwd: string;
}

// --- Scheduled Tasks & Inbox ---

/** A recurring task the agent runs on a schedule. See sidecar `schedulestore.rs`. */
export interface ScheduledTask {
  id: string;
  title: string;
  /** The objective sent to the agent on each run. */
  prompt: string;
  trigger_type: string; // "time" for v1
  interval_minutes: number | null;
  daily_time: string | null; // "HH:MM"
  daily_weekdays: number[] | null; // 0=Sun..6=Sat
  enabled: boolean;
  notify_channel: string; // "inbox"
  model: string | null; // null = default model
  created_at: number;
  updated_at: number;
  last_run_at: number | null;
  next_run_at: number | null;
  /** The most recent run's automation chat id (deep-link target). */
  last_chat_id: string | null;
}

/** A message the agent pushes to the user unprompted. */
export interface InboxItem {
  id: string;
  task_id: string | null;
  chat_id: string | null;
  kind: "summary" | "flag" | "question" | "error";
  title: string;
  body: string;
  created_at: number;
  read: boolean;
}

export interface SkillMeta {
  slug: string;
  name: string;
  description: string;
  path: string;
}

export interface OnboardingStatus {
  completed: boolean;
  skipped?: boolean;
  zwork_md_path?: string;
  zwork_md_exists?: boolean;
}

/**
 * cua-driver TCC permission status. This is the DRIVER's identity
 * (`com.trycua.driver`), not zWork's own — the driver is the process that
 * actually performs Accessibility + CGEvent work, so its grants are the source
 * of truth for whether desktop control will work. `driver_ok` is false when
 * CuaDriver.app isn't installed or the daemon can't be reached.
 *
 * `wrong_identity_hint`, when present, signals the classic "granted
 * Accessibility to zWork, not CuaDriver" trap: the driver reports a missing
 * grant while the TCC identity measured doesn't look like the driver's. The
 * UI renders this verbatim as an amber banner.
 */
export interface DesktopStatus {
  driver_ok: boolean;
  accessibility: boolean;
  screen_recording: boolean;
  source?: string;
  error?: string;
  /** Ready-to-render message when the grant looks mis-attributed (zWork not CuaDriver). */
  wrong_identity_hint?: string | null;
}

export interface OnboardingAnswer {
  key: string;
  question: string;
  answer: string;
}

export interface OnboardingCredential {
  shape: "anthropic" | "openai";
  credential: "anthropic" | "openai" | "claude_code" | "zwork_router";
  api_key: string;
  base_url: string;
  model_id: string;
  model_name: string;
}

export interface OnboardingPayload {
  answers: OnboardingAnswer[];
  credential?: OnboardingCredential;
  prefer_theme?: "light" | "dark" | "system";
  telemetry_enabled?: boolean;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  created_at: number;
  updated_at: number;
  chat_ids: string[];
  starred?: boolean;
  icon?: string;
}

export interface UploadedFile {
  client_id?: string | null;
  name: string;
  path: string;
  mime: string;
  kind: string;
  size: number;
}

export const api = {
  health: healthFetch,

  answerQuestion: (chatId: string, answer: string, questionId?: string) =>
    localFetch(`/api/chats/${chatId}/answer-question`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ answer, question_id: questionId }),
    }).then((r) => j<{ status: string }>(r)),

  approveGate: (chatId: string, gateId: string) =>
    localFetch(`/api/chats/${chatId}/gate/${gateId}/approve`, {
      method: "POST",
    }).then((r) => j<{ status: string }>(r)),

  rejectGate: (chatId: string, gateId: string) =>
    localFetch(`/api/chats/${chatId}/gate/${gateId}/reject`, {
      method: "POST",
    }).then((r) => j<{ status: string }>(r)),

  stopChat: (chatId: string) =>
    localFetch(`/api/chats/${chatId}/stop`, {
      method: "POST",
    }).then((r) =>
      j<{ ok?: boolean; success?: boolean; message?: string; steer?: string[]; follow_up?: string[] }>(r),
    ),

  // --- Queue while busy (steer / follow-up / next-run) ---

  /** Steer the live run: the message joins the in-flight context at its
   *  next checkpoint drain. */
  steerChat: (chatId: string, message: string) =>
    localFetch(`/api/chats/${chatId}/steer`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ message }),
    }).then((r) => j<QueueOutcome<string>>(r)),

  /** Queue a follow-up: it drives a next run when the current one finishes. */
  queueFollowUp: (chatId: string, message: string) =>
    localFetch(`/api/chats/${chatId}/follow-up`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ message }),
    }).then((r) => j<QueueOutcome<string>>(r)),

  /** Queue a whole next run after the current one settles. */
  queueNextRun: (chatId: string, message: string) =>
    localFetch(`/api/chats/${chatId}/next-run`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ message }),
    }).then((r) => j<QueueOutcome<string>>(r)),

  /** Snapshot the live run's queued items (kind + text per entry). */
  listChatQueue: (chatId: string) =>
    localFetch(`/api/chats/${chatId}/queue`).then((r) => j<QueueOutcome<QueuedItem[]>>(r)),

  /** Cancel one queued item. "cancelled"/"not_found" mean the text can be
   *  restored to the composer; "consumed" means it already joined the run. */
  cancelChatQueue: (chatId: string, entryId: string) =>
    localFetch(`/api/chats/${chatId}/queue/${entryId}/cancel`, {
      method: "POST",
    }).then((r) => j<QueueOutcome<"cancelled" | "consumed" | "not_found">>(r)),

  /** Set steering / follow-up queue modes ("all" | "one-at-a-time"). */
  setChatQueueMode: (chatId: string, modes: { steering?: "all" | "one-at-a-time"; followUp?: "all" | "one-at-a-time" }) =>
    localFetch(`/api/chats/${chatId}/queue/mode`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ steering: modes.steering, follow_up: modes.followUp }),
    }).then((r) => j<QueueOutcome<null>>(r)),

  truncateMessage: (chatId: string, messageId: string, content: string) => {
    return localFetch(`/api/chats/${chatId}/messages/${messageId}/truncate`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content }),
    }).then((r) => j<{ ok: boolean; chat: any }>(r));
  },

  forkChat: (chatId: string, atMessageId?: string, before?: boolean) =>
    localFetch(`/api/chats/${chatId}/fork`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ at_message_id: atMessageId, before }),
    }).then((r) => j<{ success: boolean; chat: any; error?: string }>(r)),

  /** Opens a file the agent made in the user's own app, or shows it in the folder. */
  openFile: async (path: string, reveal = false) => {
    const r = await localFetch("/api/files/open", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, reveal }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || "Couldn't open the file.");
    return body as { ok: boolean; path: string; revealed: boolean };
  },

  statFiles: (paths: string[]) =>
    localFetch("/api/files/stat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paths }),
    }).then((r) => j<{ existing: string[] }>(r)),

  listBranches: (chatId: string) =>
    localFetch(`/api/chats/${chatId}/branches`).then((r) =>
      j<{ branches: { id: string; created_at: number; from_message_id: string; message_count: number; preview: string }[] }>(r),
    ),

  restoreBranch: (chatId: string, branchId: string) =>
    localFetch(`/api/chats/${chatId}/branches/${branchId}`, { method: "POST" }).then((r) =>
      j<{ success: boolean; chat: any }>(r),
    ),

  deleteBranch: (chatId: string, branchId: string) =>
    localFetch(`/api/chats/${chatId}/branches/${branchId}`, { method: "DELETE" }).then((r) =>
      j<{ success: boolean; chat: any }>(r),
    ),

  runPythonCode: (code: string) => {
    return localFetch("/api/run-python", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    }).then((r) => j<{ stdout: string; stderr: string }>(r));
  },

  captureScreenshot: () =>
    localFetch("/api/screenshot", {
      method: "POST"
    }).then((r) => j<{ screenshot: string; error?: string }>(r)),

  exportDocx: (title: string, content: string) =>
    localFetch("/api/export/docx", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title, content })
    }).then((r) => r.blob()),

  exportPdf: (title: string, content: string) =>
    localFetch("/api/export/pdf", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title, content })
    }).then((r) => r.blob()),

  getActivityLogs: () =>
    localFetch("/api/activity-logs").then((r) =>
      j<{ logs: Array<{ timestamp: number; filename: string; path: string }> }>(r)
    ),

  waitForBackend: waitForBackendReady,

  // --- Tasks & Calendar ---
  listTasks: () =>
    localFetch("/api/tasks").then((r) => j<{ tasks: any[] }>(r)),

  createTask: (body: { title: string; column?: string; due_date?: string | null; description?: string; assignee?: string; priority?: string }) =>
    localFetch("/api/tasks", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ task: any }>(r)),

  updateTask: (id: string, body: { title: string; column: string; due_date?: string | null; description?: string; assignee?: string; priority?: string }) =>
    localFetch(`/api/tasks/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ task: any }>(r)),

  updateTaskColumn: (id: string, column: string) =>
    localFetch(`/api/tasks/${id}/column`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ column }),
    }).then((r) => j<{ task: any }>(r)),

  deleteTask: (id: string) =>
    localFetch(`/api/tasks/${id}`, {
      method: "DELETE",
    }).then((r) => j<{ ok: boolean }>(r)),

  listEvents: () =>
    localFetch("/api/events").then((r) => j<{ events: any[] }>(r)),

  createEvent: (body: { title: string; date: string; start_time?: string | null; end_time?: string | null }) =>
    localFetch("/api/events", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ event: any }>(r)),

  deleteEvent: (id: string) =>
    localFetch(`/api/events/${id}`, {
      method: "DELETE",
    }).then((r) => j<{ ok: boolean }>(r)),

  // --- Scheduled Tasks ---
  listSchedules: () =>
    localFetch("/api/schedules").then((r) => j<{ tasks: ScheduledTask[] }>(r)),

  createSchedule: (body: {
    title: string;
    prompt: string;
    interval_minutes?: number;
    daily_time?: string;
    daily_weekdays?: number[];
    enabled?: boolean;
    model?: string;
  }) =>
    localFetch("/api/schedules", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ task: ScheduledTask; error?: string }>(r)),

  updateSchedule: (
    id: string,
    body: {
      title?: string;
      prompt?: string;
      interval_minutes?: number | null;
      daily_time?: string | null;
      daily_weekdays?: number[] | null;
      enabled?: boolean;
      model?: string | null;
    },
  ) =>
    localFetch(`/api/schedules/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ task: ScheduledTask; error?: string }>(r)),

  deleteSchedule: (id: string) =>
    localFetch(`/api/schedules/${id}`, { method: "DELETE" }).then((r) =>
      j<{ success: boolean }>(r),
    ),

  /** Manually trigger a scheduled task run ("Run now"). */
  runScheduleNow: (id: string) =>
    localFetch(`/api/schedules/${id}/run`, { method: "POST" }).then((r) =>
      j<{ success: boolean; error?: string }>(r),
    ),

  // --- Inbox ---
  listInbox: (unreadOnly = false) =>
    localFetch(`/api/inbox${unreadOnly ? "?unread=true" : ""}`).then((r) =>
      j<{ items: InboxItem[]; unread_count: number }>(r),
    ),

  markInboxRead: (id: string) =>
    localFetch(`/api/inbox/${id}`, { method: "PATCH" }).then((r) =>
      j<{ success: boolean }>(r),
    ),

  markAllInboxRead: () =>
    localFetch("/api/inbox/all-read", { method: "POST" }).then((r) =>
      j<{ success: boolean; marked_read: number }>(r),
    ),

  deleteInboxItem: (id: string) =>
    localFetch(`/api/inbox/${id}`, { method: "DELETE" }).then((r) =>
      j<{ success: boolean }>(r),
    ),

  me: () => localFetch("/api/me").then((r) => j<MeResponse>(r)),

  integrations: () =>
    localFetch("/api/integrations").then((r) =>
      j<{ integrations: Integration[] }>(r),
    ),

  composioStatus: () =>
    localFetch("/api/composio/status").then((r) => j<ComposioStatus>(r)),

  composioSetConfig: (body: { enabled?: boolean; api_key?: string }) =>
    localFetch("/api/composio/config", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ ok: boolean; status: ComposioStatus }>(r)),

  composioAccounts: () =>
    localFetch("/api/composio/accounts").then((r) =>
      j<{ accounts: ComposioAccount[] }>(r),
    ),

  composioConnect: (app: string) =>
    localFetch("/api/composio/connect", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ app }),
    }).then((r) => j<{ url: string }>(r)),

  composioDisconnect: (app: string) =>
    localFetch("/api/composio/disconnect", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ app }),
    }).then((r) => j<{ ok: boolean; connected_apps: string[] }>(r)),

  composioApps: () =>
    localFetch("/api/composio/apps").then((r) =>
      j<{ apps: ComposioApp[] }>(r),
    ),

  providers: () =>
    localFetch("/api/providers").then((r) => j<ProvidersResponse>(r)),

  getSettings: () =>
    localFetch("/api/settings").then((r) => j<SettingsPublic>(r)),

  putSettings: (patch: Partial<{
    api_keys: Record<string, string>;
    provider_config: Record<string, Record<string, string>>;
    default_model: string;
    use_claude_code_config: boolean;
    telemetry_enabled: boolean;
  }>) =>
    localFetch("/api/settings", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    }).then((r) => j<SettingsPublic>(r)),

  telemetryEvent: (body: {
    event: string;
    session_id?: string;
    properties?: Record<string, unknown>;
    ts?: number;
  }) =>
    localFetch("/api/telemetry/event", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).then((r) => j<{ ok: boolean }>(r)),

  providerCatalog: () =>
    localFetch("/api/providers/catalog").then((r) => j<{ providers: CatalogProvider[] }>(r)),

  providerCatalogModels: (id: string) =>
    localFetch(`/api/providers/catalog/${encodeURIComponent(id)}`).then((r) =>
      j<{ id: string; name: string; models: CatalogModel[] }>(r),
    ),

  /** Slash commands: prompt files from zWork/Claude Code/opencode/pi folders, and skills. */
  commands: () =>
    // The web app has no sidecar to read command folders from.
    (IS_WEB ? Promise.resolve(new Response('{"commands":[]}')) : localFetch("/api/commands")).then((r) =>
      j<{ commands: { name: string; description: string; kind: "command" | "skill"; source: string }[] }>(r),
    ),

  /** zWork's managed Python + Node (installed on first launch). */
  runtimeStatus: () => localFetch("/api/runtime").then((r) => j<{ status: RuntimeStatus; dir: string }>(r)),

  runtimeInstall: () =>
    localFetch("/api/runtime", { method: "POST" }).then((r) => j<{ status: RuntimeStatus }>(r)),

  mcpServers: () =>
    localFetch("/api/mcp/servers").then((r) => j<{ servers: McpServer[]; config_path: string }>(r)),

  /** Add one server (`name` + `config`) or a pasted JSON snippet; connects before replying. */
  mcpAdd: (body: { name?: string; config?: Record<string, unknown>; paste?: string }) =>
    localFetch("/api/mcp/servers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => r.json() as Promise<McpAddResult>),

  mcpRemove: (name: string) =>
    localFetch(`/api/mcp/servers/${encodeURIComponent(name)}`, { method: "DELETE" }).then((r) =>
      j<{ success: boolean }>(r),
    ),

  mcpSetEnabled: (name: string, enabled: boolean) =>
    localFetch(`/api/mcp/servers/${encodeURIComponent(name)}/enabled`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    }).then((r) => j<{ success: boolean; error?: string | null }>(r)),

  mcpConnect: (name: string) =>
    localFetch(`/api/mcp/servers/${encodeURIComponent(name)}/connect`, { method: "POST" }).then((r) =>
      j<{ success: boolean; error?: string }>(r),
    ),

  mcpDiscover: () => localFetch("/api/mcp/discover").then((r) => j<{ sources: McpDiscoverySource[] }>(r)),

  mcpImport: (source: string, names: string[]) =>
    localFetch("/api/mcp/import", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source, names }),
    }).then((r) => j<{ success: boolean; imported?: string[]; error?: string }>(r)),

  upsertCustomModel: (body: Omit<CustomModel, "id"> & { id?: string }) =>
    localFetch("/api/custom-models", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ custom_models: CustomModel[]; id: string }>(r)),

  deleteCustomModel: (id: string) =>
    localFetch(`/api/custom-models/${id}`, { method: "DELETE" }).then((r) =>
      j<{ custom_models: CustomModel[] }>(r),
    ),

  listChats: () => {
    const base = IS_WEB ? "/api/web/chats" : "/api/chats";
    return localFetch(base).then((r) => j<{ chats: ApiChatSummary[] }>(r));
  },

  getChat: (id: string) => {
    const base = IS_WEB ? `/api/web/chats/${id}` : `/api/chats/${id}`;
    return localFetch(base).then((r) => j<ApiChat>(r));
  },

  deleteChat: (id: string) => {
    const base = IS_WEB ? `/api/web/chats/${id}` : `/api/chats/${id}`;
    return localFetch(base, { method: "DELETE" }).then((r) =>
      j<{ ok: boolean }>(r),
    );
  },

  renameChat: (id: string, title: string) => {
    const base = IS_WEB ? `/api/web/chats/${id}` : `/api/chats/${id}`;
    return localFetch(base, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title }),
    }).then((r) => j<ApiChat>(r));
  },

  /**
   * Persist edits to an individual stored message (content and/or activities).
   * Used to write artifact edits back to disk so they survive restart.
   * No-op in web mode (web chats are ephemeral).
   */
  patchMessage: (
    chatId: string,
    messageId: string,
    patch: { content?: string; activities?: Array<{ id: string; label: string; icon?: string; done: boolean }> },
  ) => {
    if (IS_WEB) return Promise.resolve({ ok: true });
    return localFetch(`/api/chats/${chatId}/messages/${messageId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    }).then((r) => j<{ ok: boolean }>(r));
  },

  // ---- Web chat persistence (Axum API) ----
  webCreateChat: (title: string) =>
    localFetch("/api/web/chats", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ title }),
    }).then((r) => j<{ id: string; title: string; created_at: string; updated_at: string }>(r)),

  webAddMessage: (chatId: string, role: string, content: string) =>
    localFetch(`/api/web/chats/${chatId}/messages`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ role, content }),
    }).then((r) => j<{ id: string; chat_id: string; role: string; content: string; created_at: string }>(r)),

  // ---- Desktop control / driver permissions ----
  desktopStatus: () =>
    localFetch("/api/desktop/status").then((r) => j<DesktopStatus>(r)),

  desktopGrant: () =>
    localFetch("/api/desktop/permissions/grant", { method: "POST" }).then((r) =>
      j<DesktopStatus>(r),
    ),

  /** List on-screen windows for the "Share Window" picker. On macOS this
   *  routes through the Tauri host (CoreGraphics FFI) so zWork uses its OWN
   *  Screen Recording grant; Windows and Linux need no grant and go through the
   *  backend's cua-driver. Returns an empty list off Tauri. */
  listWindows: async () => {
    if (!IS_TAURI) return { windows: [] };
    if (!isMacOS()) {
      return localFetch("/api/desktop/windows").then((r) =>
        j<{ windows: Array<{ window_id: number; pid: number; app_name: string; title: string }>; error?: string }>(r),
      );
    }
    try {
      const windows = await invoke<Array<{ window_id: number; pid: number; app_name: string; title: string }>>(
        "list_windows_native",
      );
      return { windows };
    } catch (e) {
      return { windows: [], error: e instanceof Error ? e.message : String(e) };
    }
  },

  /** Whether zWork itself has Screen Recording permission (macOS only). Used by
   *  the Share Window picker to decide whether to prompt for the grant. */
  screenCapturePreflight: () =>
    IS_TAURI
      ? isMacOS()
        ? invoke<boolean>("screen_capture_preflight").catch(() => false)
        : Promise.resolve(true)
      : Promise.resolve(false),

  /** Trigger the native macOS "allow Screen Recording" prompt (prompting). macOS
   *  only honors it on first launch; pair with openScreenRecordingSettings for
   *  re-grants. Returns the post-prompt permission state. */
  requestScreenCapture: () =>
    IS_TAURI
      ? invoke<boolean>("request_screen_capture").catch(() => false)
      : Promise.resolve(false),

  /** Open the macOS Screen Recording privacy pane so the user can grant zWork.
   *  No-op off Tauri / non-macOS. */
  openScreenRecordingSettings: () =>
    IS_TAURI
      ? invoke("open_macos_privacy_pane", { pane: "screen_recording" }).catch(() => {})
      : Promise.resolve(),

  /** Capture a screenshot of a specific window (macOS only, needs Screen
   *  Recording granted to zWork). Returns `{ data_url, mime }` or `{ error }`. */
  captureWindow: async (windowId: number) => {
    if (!IS_TAURI) return { error: "Desktop capture is only available in the app." };
    if (!isMacOS()) {
      return localFetch(`/api/desktop/windows/${windowId}/screenshot`, { method: "POST" }).then((r) =>
        j<{ data_url?: string; mime?: string; error?: string }>(r),
      );
    }
    try {
      const result = await invoke<{ data_url: string; mime?: string }>(
        "capture_window_native",
        // Tauri v2 binds invoke args by the Rust parameter name (snake_case).
        // The Rust command is `fn capture_window_native(window_id: i64)`, so
        // the arg key MUST be `window_id` — `windowId` silently fails to bind.
        { window_id: windowId },
      );
      return { data_url: result.data_url, mime: result.mime || "image/png" };
    } catch (e) {
      return { error: e instanceof Error ? e.message : String(e) };
    }
  },

  browserBridgeStatus: () =>
    localFetch("/api/browser-bridge/status").then((r) =>
      j<{ connected: boolean }>(r),
    ),

  // ---- Skills + onboarding ----
  skills: () =>
    localFetch("/api/skills").then((r) => j<{ skills: SkillMeta[] }>(r)),
  onboardStatus: () =>
    localFetch("/api/onboard/status").then((r) => j<OnboardingStatus>(r)),

  onboardSkip: () =>
    localFetch("/api/onboard/skip", { method: "POST" }).then((r) =>
      j<{ ok: boolean }>(r),
    ),

  onboardComplete: (body: OnboardingPayload) =>
    localFetch("/api/onboard/complete", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) =>
      j<{ ok: boolean; zwork_md_path: string; preview: string }>(r),
    ),

  // ---- Memory ----
  getMemory: () =>
    localFetch("/api/memory").then((r) => j<{ content: string }>(r)),

  putMemory: (content: string) =>
    localFetch("/api/memory", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content }),
    }).then((r) => j<{ ok: boolean }>(r)),

  // ---- User MD (zwork.md) ----
  getUserMd: () =>
    localFetch("/api/user-md").then((r) => j<{ content: string }>(r)),

  putUserMd: (content: string) =>
    localFetch("/api/user-md", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content }),
    }).then((r) => j<{ ok: boolean }>(r)),

  uploadFiles: (files: Array<{
    client_id?: string | null;
    name: string;
    mime: string;
    kind: string;
    text_content?: string | null;
    data_url?: string | null;
  }>) =>
    localFetch("/api/uploads", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ files }),
    }).then((r) => j<{ files: UploadedFile[] }>(r)),

  listUploads: () =>
    localFetch("/api/uploads").then((r) =>
      j<{ files: Array<{ name: string; size: number; mime: string; content: string; path: string }> }>(r)
    ),

  // ---- Projects ----
  listProjects: () =>
    localFetch("/api/projects").then((r) => j<{ projects: Project[] }>(r)),

  createProject: (name: string, description?: string, icon?: string) =>
    localFetch("/api/projects", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, description: description || "", ...(icon ? { icon } : {}) }),
    }).then((r) => j<{ project: Project }>(r)),

  updateProject: (id: string, data: { name?: string; description?: string; starred?: boolean; icon?: string }) =>
    localFetch(`/api/projects/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    }).then((r) => j<{ project: Project }>(r)),

  deleteProject: (id: string) =>
    localFetch(`/api/projects/${id}`, { method: "DELETE" }).then((r) =>
      j<{ ok: boolean }>(r),
    ),

  getProjectContext: (id: string) =>
    localFetch(`/api/projects/${id}/context`).then((r) =>
      j<{ content: string }>(r),
    ),

  ollamaModels: (base_url: string, api_key: string) =>
    localFetch(`/api/ollama/models`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ base_url, api_key }),
    }).then((r) =>
      j<{ models: { id: string; name: string }[]; error?: string }>(r),
    ),

  /**
   * Pull an Ollama model with streaming progress. Calls POST /api/ollama/pull
   * and reads the SSE stream, invoking `onProgress` for each status record
   * (e.g. {status:"downloading", total, completed}, {status:"success"}).
   * Resolves when the stream ends ([DONE]); rejects on connection error.
   */
  ollamaPull: async (
    model: string,
    base_url: string,
    api_key: string,
    onProgress: (rec: OllamaPullProgress) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    const resp = await localFetch(`/api/ollama/pull`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ model, base_url, api_key }),
      signal,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `Pull failed (${resp.status})`);
    }
    if (!resp.body) throw new Error("No response stream");
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE events are separated by blank lines; each has `data: <payload>`.
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const block = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const dataLine = block
          .split("\n")
          .find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        const payload = dataLine.slice(5).trim();
        if (payload === "[DONE]") return;
        try {
          const rec = JSON.parse(payload) as OllamaPullProgress;
          onProgress(rec);
          if (rec.status === "success") return;
          if (rec.error) throw new Error(rec.error);
        } catch (e) {
          if (e instanceof Error && e.message) throw e;
          // Non-JSON line — ignore (keepalive etc.)
        }
      }
    }
  },

  putProjectContext: (id: string, content: string) =>
    localFetch(`/api/projects/${id}/context`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content }),
    }).then((r) => j<{ ok: boolean }>(r)),

  getProjectMemory: (id: string) =>
    localFetch(`/api/projects/${id}/memory`).then((r) =>
      j<{ content: string }>(r),
    ),

  putProjectMemory: (id: string, content: string) =>
    localFetch(`/api/projects/${id}/memory`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content }),
    }).then((r) => j<{ ok: boolean }>(r)),

  getProjectTimeline: (id: string) =>
    localFetch(`/api/projects/${id}/timeline`).then((r) =>
      j<{ content: string }>(r),
    ),

  refactor: (body: {
    code: string;
    instruction: string;
    mode?: string;
    model?: string;
  }) =>
    localFetch("/api/refactor", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) =>
      j<{ refactored_code: string; explanation: string; steps: string[] }>(r)
    ),

  scrape: (url: string) =>
    localFetch("/api/scrape", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ url }),
    }).then((r) => j<{ markdown: string; title: string }>(r)),

  getProjectFiles: (projectId: string) =>
    localFetch(`/api/projects/${projectId}/files`).then((r) =>
      j<{ files: Array<{ name: string; size: number; mime: string; path: string }> }>(r),
    ),

  uploadProjectFiles: (projectId: string, body: { files: Array<{ name: string; mime: string; kind: string; data_url: string }> }) =>
    localFetch(`/api/projects/${projectId}/files`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => j<{ files: Array<{ name: string; size: number; mime: string; path: string }> }>(r)),

  deleteProjectFile: (projectId: string, filename: string) =>
    localFetch(`/api/projects/${projectId}/files/${filename}`, {
      method: "DELETE",
    }).then((r) => j<{ ok: boolean }>(r)),
};

// ------ SSE streaming for chat ------

/** Envelope for the queue-while-busy endpoints: `queued` tells whether a
 *  live run accepted the item (`not-busy` = no run to queue against). */
export interface QueueOutcome<T> {
  queued: boolean;
  result?: T;
  reason?: string;
  error?: string;
}

/** One queued message on the live run, as the backend reports it. */
export interface QueuedItem {
  entry_id: string;
  kind: "steer" | "follow_up" | "next_run" | "write";
  text: string;
}

export type StreamEvent =
  | { type: "chat"; id: string; title: string }
  | { type: "status"; text: string }
  | { type: "delta"; text: string }
  | { type: "thinking_delta"; text: string }
  | { type: "thinking_end" }
  | { type: "meta"; provider: string; resolved_model: string; upstream_provider?: string }
  | { type: "done" }
  | { type: "end" }
  | { type: "heartbeat" }
  | { type: "error"; text: string }
  | { type: "needs_setup" }
  | { type: "activity"; id: string; label: string; icon?: string; done?: boolean; tool_use_id?: string }
  | { type: "tool_use"; id: string; name: string; input?: unknown }
  | { type: "tool_result"; tool: string; ok: boolean; message: string; tool_use_id?: string }
  | { type: "tool_progress"; tool_id: string; label: string }
  | { type: "tool_start"; tool: string; input?: unknown }
  | { type: "tool_complete"; tool: string; ok: boolean; message: string }
  | { type: "usage"; prompt_tokens?: number; completion_tokens?: number; total_tokens?: number }
  | { type: "permission"; tool: string; risk: "safe" | "sensitive" | "destructive"; reason: string; blocked: boolean; gate_id?: string; tool_use_id?: string }
  | { type: "compaction"; status: "complete" | "failed"; before_chars?: number; after_chars?: number; model?: string; error?: string }
  | { type: "subagent_started"; task_id: string; description: string }
  | { type: "subagent_progress"; task_id: string; status: "pending" | "running" | "completed" | "failed" }
  | { type: "subagent_delta"; task_id: string; text: string }
  | { type: "subagent_activity"; task_id: string; event: StreamEvent }
  | { type: "subagent_done"; task_id: string; result?: string; error?: string }
  | { type: "ask_question"; chat_id: string; question_id?: string; question: string; options: string[] }
  | { type: "permission_recovery"; tool_use_id?: string; kind?: string; message: string }
  | { type: "todo_update"; todos: Array<{ id: string; content: string; status: "pending" | "in_progress" | "completed" }> }
  | { type: "queue"; items: QueuedItem[] }
  | { type: "user_message"; text: string; assistant_id?: string }
  | { type: "run_state"; live: boolean; run_id?: string; cursor?: number; started_at?: number };

/** The web app's only context: who it is and the current date and time. */
function webSystemPrompt(): string {
  const now = new Date();
  const date = now.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return [
    "You are zWork, a helpful AI work assistant.",
    `Today is ${date}. The user's local time is ${time}.`,
    "This is the zWork web app: you can read text and images the user sends, but you cannot browse, open files or use tools here. If a task needs that, say the zWork desktop app can do it.",
    "Reply in the language the user writes in. Be concise and direct.",
  ].join("\n");
}

/** A router refusal in words a user can act on. */
function routerErrorText(status: number, body: string): string {
  const code = body.trim().replace(/^"+|"+$/g, "");
  switch (code) {
    case "root_request_quota_exceeded":
      return "You've used this period's allowance. It refills over the next few hours; upgrading raises it.";
    case "model_requires_pro_tier":
      return "That model needs the Pro plan.";
    case "model_requires_max_tier":
      return "That model needs the Max plan.";
    case "too_many_active_runs":
      return "Another reply is still running. Wait for it to finish, then try again.";
    case "not_signed_in":
    case "gateway_access_denied":
      return "Your session expired. Sign in again.";
    default:
      return `${status}: ${code || "Request failed"}`;
  }
}

/** Web-mode streaming: a plain chat completion through the router, its SSE
 *  translated into the event format the UI expects. */
async function streamChatWeb(
  body: {
    chat_id?: string;
    run_id?: string;
    message: string;
    model?: string;
    artifact_mode?: boolean;
    project_id?: string;
    plan_mode?: boolean;
    auto_approve_destructive?: boolean;
    attachments?: Array<{
      client_id?: string | null;
      name: string;
      path: string;
      data_url?: string;
      mime: string;
      kind: string;
    }>;
    web_search_enabled?: boolean;
    effort?: string;
    history?: { role: "user" | "assistant"; content: string }[];
  },
  onEvent: (evt: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const cloudToken = typeof window !== "undefined"
    ? window.localStorage.getItem("zwork:cloud-token") || ""
    : "";

  // The web app is a plain chat on the auto router: no tools, so it stays on
  // the cheap model, and no ?????? (that needs the desktop app).
  const friendlyModel = "zwork-auto";

  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...clientHeaders(),
    ...(body.run_id ? { "x-zwork-run-id": body.run_id } : {}),
    ...(body.chat_id ? { "x-zwork-chat-id": body.chat_id } : {}),
    ...(body.project_id ? { "x-zwork-project-id": body.project_id } : {}),
  };
  if (cloudToken) headers["authorization"] = `Bearer ${cloudToken}`;

  const images = (body.attachments ?? []).filter((a) => a.mime.startsWith("image/") && a.data_url);
  const userContent = images.length
    ? [
        ...(body.message ? [{ type: "text" as const, text: body.message }] : []),
        ...images.map((a) => ({ type: "image_url" as const, image_url: { url: a.data_url } })),
      ]
    : body.message;

  const upstreamBody = {
    model: friendlyModel,
    messages: [
      { role: "system" as const, content: webSystemPrompt() },
      ...(body.history ?? []),
      { role: "user" as const, content: userContent },
    ],
    stream: true,
    max_tokens: 16384,
    ...(body.effort ? { reasoning: { effort: body.effort } } : {}),
  };

  const webChatTitle = body.message
    ? body.message.slice(0, 56)
    : body.attachments?.some((a) => a.kind === "image")
      ? "Image"
      : "New chat";

  // Create or reuse a server-side web chat for persistence
  let serverChatId = body.chat_id;
  if (!serverChatId || serverChatId.startsWith("tmp_") || serverChatId.startsWith("web_")) {
    try {
      const chat = await fetch(u("/api/web/chats"), {
        ...WEB_CREDENTIALS,
        method: "POST",
        headers,
        body: JSON.stringify({ title: webChatTitle }),
      }).then((r) => r.json() as Promise<{ id: string }>);
      serverChatId = chat.id;
    } catch { /* persistence failure is non-fatal */ }
  }

  // Save user message to server
  if (serverChatId) {
    try {
      await fetch(u(`/api/web/chats/${serverChatId}/messages`), {
        ...WEB_CREDENTIALS,
        method: "POST",
        headers,
        body: JSON.stringify({ role: "user", content: body.message || webChatTitle }),
      });
    } catch { /* non-fatal */ }
  }

  onEvent({ type: "chat", id: serverChatId || `web_${Date.now()}`, title: webChatTitle });
  onEvent({ type: "status", text: "Thinking" });

  const resp = await fetch(u("/api/v1/chat/completions"), {
    ...WEB_CREDENTIALS,
    method: "POST",
    headers,
    body: JSON.stringify(upstreamBody),
    signal,
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    onEvent({ type: "error", text: routerErrorText(resp.status, text) });
    onEvent({ type: "end" });
    return;
  }

  // Extract provider/model from response headers. The router may echo the
  // upstream model id in x-zwork-router-model; map it back to the friendly
  // zWork name so the upstream provider is never surfaced to the user.
  const provider = resp.headers.get("x-zwork-router-provider") || "zwork-router";
  const rawRouterModel = resp.headers.get("x-zwork-router-model");
  const displayModel = whitelabelModelName(rawRouterModel) ?? friendlyModel;
  onEvent({ type: "meta", provider, resolved_model: displayModel, upstream_provider: provider });

  onEvent({ type: "status", text: "Drafting" });

  let assistantText = "";

  if (resp.body) {
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    // The router's /api/v1/chat/completions returns OpenAI-shaped SSE
    // ({"choices":[{"delta":{...}}]} + "data: [DONE]"); the Anthropic-shaped
    // /api/v1/messages path returns content_block_delta/message_stop events.
    // Handle BOTH so either endpoint feeds the UI.
    let sawMessageStop = false;
    const handleParsedChunk = (chunk: any) => {
      // Anthropic shape
      if (chunk.type === "content_block_delta" && chunk.delta?.type === "text_delta" && chunk.delta?.text) {
        assistantText += chunk.delta.text;
        onEvent({ type: "delta", text: chunk.delta.text });
      }
      if (chunk.type === "content_block_delta" && chunk.delta?.type === "thinking_delta" && chunk.delta?.thinking) {
        onEvent({ type: "thinking_delta", text: chunk.delta.thinking });
      }
      // OpenAI shape
      if (Array.isArray(chunk.choices) && chunk.choices.length > 0) {
        const delta = chunk.choices[0]?.delta;
        if (delta?.content) {
          assistantText += delta.content;
          onEvent({ type: "delta", text: delta.content });
        }
        // OpenRouter / DeepSeek reasoning fields.
        const reasoning = delta?.reasoning_content ?? delta?.reasoning;
        if (typeof reasoning === "string" && reasoning) {
          onEvent({ type: "thinking_delta", text: reasoning });
        }
      }
      if (chunk.type === "message_stop") {
        sawMessageStop = true;
      }
    };
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n")) >= 0) {
        const line = buf.slice(0, idx).trim();
        buf = buf.slice(idx + 1);
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (!data) continue;
        if (data === "[DONE]") {
          sawMessageStop = true;
          continue;
        }
        try {
          handleParsedChunk(JSON.parse(data));
        } catch { /* ignore malformed */ }
        if (sawMessageStop) break;
      }
    }
  } else {
    const text = await resp.text();
    for (const line of text.split("\n")) {
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (!data || data === "[DONE]") continue;
      try {
        const chunk = JSON.parse(data);
        if (chunk.type === "content_block_delta" && chunk.delta?.type === "text_delta" && chunk.delta?.text) {
          assistantText += chunk.delta.text;
          onEvent({ type: "delta", text: chunk.delta.text });
        }
        if (chunk.type === "content_block_delta" && chunk.delta?.type === "thinking_delta" && chunk.delta?.thinking) {
          onEvent({ type: "thinking_delta", text: chunk.delta.thinking });
        }
        if (Array.isArray(chunk.choices) && chunk.choices.length > 0) {
          const delta = chunk.choices[0]?.delta;
          if (delta?.content) {
            assistantText += delta.content;
            onEvent({ type: "delta", text: delta.content });
          }
          const reasoning = delta?.reasoning_content ?? delta?.reasoning;
          if (typeof reasoning === "string" && reasoning) {
            onEvent({ type: "thinking_delta", text: reasoning });
          }
        }
      } catch { /* ignore */ }
    }
  }

  // Save assistant response to server
  if (serverChatId && assistantText) {
    try {
      await fetch(u(`/api/web/chats/${serverChatId}/messages`), {
        ...WEB_CREDENTIALS,
        method: "POST",
        headers,
        body: JSON.stringify({ role: "assistant", content: assistantText }),
      });
    } catch { /* non-fatal */ }
  }

  onEvent({ type: "done" });
  onEvent({ type: "end" });
}

/**
 * Demo-mode streaming: posts to the public, unauthenticated /api/demo/chat
 * endpoint and translates the Anthropic-shaped SSE into the StreamEvent union
 * the UI expects. Mirrors streamChatWeb's event mapping but:
 *   - sends no Authorization header (anonymous)
 *   - sends only { messages } — the server hardcodes model + system prompt
 *   - performs no /api/web/chats persistence (demo is ephemeral)
 *   - surfaces 429/503 as friendly errors matching the demo's limits
 *
 * `body.attachments` is ignored: the demo endpoint is text-only. Multi-turn
 * context is reconstructed from the store's prior messages by the caller (the
 * `send` action already passes only the new `message`; we rebuild history here
 * from nothing because the demo endpoint receives the full conversation).
 */
async function streamChatDemo(
  body: {
    chat_id?: string;
    run_id?: string;
    message: string;
    model?: string;
    artifact_mode?: boolean;
    project_id?: string;
    plan_mode?: boolean;
    auto_approve_destructive?: boolean;
    attachments?: Array<{
      client_id?: string | null;
      name: string;
      path: string;
      data_url?: string;
      mime: string;
      kind: string;
    }>;
    web_search_enabled?: boolean;
  },
  onEvent: (evt: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  // The demo endpoint takes the full conversation as { messages }. We only
  // have the current turn here; prior context is owned by the store. To keep
  // the demo multi-turn, we send just this user message — the server treats
  // each request as a single-turn exchange, which is the expected demo shape.
  // (The store's `send` reconstructs history for the gateway path; for demo
  // we deliberately keep it stateless to match the endpoint's design.)
  const messages = [{ role: "user" as const, content: body.message }];

  const resp = await fetch(u("/api/demo/chat"), {
    method: "POST",
    headers: { "content-type": "application/json", ...clientHeaders() },
    body: JSON.stringify({ messages }),
    signal,
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    let friendly: string;
    switch (resp.status) {
      case 429:
        friendly =
          "You've hit the demo's rate limit. Wait a bit and try again, or download the desktop app for unlimited use.";
        break;
      case 503:
        friendly = "The demo isn't available right now. Try again in a moment.";
        break;
      case 404:
        friendly = "The demo isn't available right now. Grab the desktop app at tryzwork.app.";
        break;
      default:
        friendly = text || `Something went wrong (HTTP ${resp.status}).`;
    }
    onEvent({ type: "error", text: friendly });
    onEvent({ type: "end" });
    return;
  }

  // Provider/model from response headers (the demo endpoint sets x-zwork-router-*).
  const provider = resp.headers.get("x-zwork-router-provider") || "zwork-demo";
  const routerModel = whitelabelModelName(resp.headers.get("x-zwork-router-model")) ?? "zwork-auto";
  onEvent({ type: "meta", provider, resolved_model: routerModel, upstream_provider: provider });
  onEvent({ type: "status", text: "Drafting" });

  if (!resp.body) {
    onEvent({ type: "error", text: "No response stream from the demo server." });
    onEvent({ type: "end" });
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let sawText = false;
  // The demo caps output tokens server-side; without this flag a long answer
  // that hits the cap just stops mid-sentence with no indication why.
  let truncatedByLimit = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, idx).trim();
      buf = buf.slice(idx + 1);
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (!data) continue;
      try {
        const chunk = JSON.parse(data);
        // Text answer deltas — same shape streamChatWeb handles.
        if (
          chunk.type === "content_block_delta" &&
          chunk.delta?.type === "text_delta" &&
          chunk.delta?.text
        ) {
          sawText = true;
          onEvent({ type: "delta", text: chunk.delta.text });
        }
        // Reasoning deltas → process panel (not blended into the answer).
        if (
          chunk.type === "content_block_delta" &&
          chunk.delta?.type === "thinking_delta" &&
          chunk.delta?.thinking
        ) {
          onEvent({ type: "thinking_delta", text: chunk.delta.thinking });
        }
        if (
          chunk.type === "message_delta" &&
          chunk.delta?.stop_reason === "max_tokens"
        ) {
          truncatedByLimit = true;
        }
        if (chunk.type === "message_stop") {
          break;
        }
      } catch {
        /* malformed SSE line — ignore */
      }
    }
  }

  if (truncatedByLimit) {
    onEvent({
      type: "error",
      text: "Demo response truncated at the length limit. Sign in or use the desktop app for full-length responses.",
    });
  } else if (!sawText) {
    onEvent({ type: "error", text: "The model returned an empty response. Try rephrasing." });
  }
  onEvent({ type: "done" });
  onEvent({ type: "end" });
}

export async function streamChat(
  body: {
    chat_id?: string;
    run_id?: string;
    message: string;
    model?: string;
    artifact_mode?: boolean;
    project_id?: string;
    plan_mode?: boolean;
    auto_approve_destructive?: boolean;
    attachments?: Array<{
      client_id?: string | null;
      name: string;
      path: string;
      data_url?: string;
      mime: string;
      kind: string;
    }>;
    web_search_enabled?: boolean;
    effort?: string;
    history?: { role: "user" | "assistant"; content: string }[];
  },
  onEvent: (evt: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  // Public no-login demo: route to the unauthenticated /api/demo/chat
  // endpoint instead of the gateway. Must come before the IS_WEB branch,
  // which targets the authenticated /api/v1/messages path.
  if (isDemoMode()) {
    return streamChatDemo(body, onEvent, signal);
  }
  if (IS_WEB) {
    return streamChatWeb(body, onEvent, signal);
  }

  let sawEvent = false;
  let sawTerminal = false;
  let sawServerError = false;
  let attemptedRecovery = false;
  let retryCount = 0;
  // Re-attach bookkeeping: the real chat id (server-assigned ids arrive on
  // the `chat` event; tmp_ placeholders never have a live run) and the last
  // bus seq seen — together they let a dropped stream resume the SAME run
  // via GET /api/chats/:id/run/live?after=<seq> instead of re-POSTing the
  // message (which would start a duplicate run and re-bill it).
  let liveChatId =
    body.chat_id && !body.chat_id.startsWith("tmp_") ? body.chat_id : undefined;
  let lastSeq: number | undefined;
  const noteEvent = (evt: any) => {
    sawEvent = true;
    if (evt.type === "chat" && typeof evt.id === "string" && !evt.id.startsWith("tmp_")) {
      liveChatId = evt.id;
    }
    if (evt.type === "done" || evt.type === "end") {
      sawTerminal = true;
    }
    if (evt.type === "error") {
      sawServerError = true;
    }
    if (typeof evt.seq === "number") {
      lastSeq = evt.seq;
    }
  };
  const parseFrame = (frame: string) => {
    for (const line of frame.split("\n")) {
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (!data) continue;
      try {
        const evt = JSON.parse(data) as StreamEvent;
        noteEvent(evt);
        onEvent(evt);
      } catch {
        /* ignore malformed partial event */
      }
    }
  };

  const readStream = async () => {
    const sidecarToken = await getSidecarToken();
    const resp = await fetch(u("/api/chat/stream"), {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...clientHeaders(),
        ...(sidecarToken ? { "x-zwork-token": sidecarToken } : {}),
        ...(body.run_id ? { "x-zwork-run-id": body.run_id } : {}),
        ...(body.chat_id ? { "x-zwork-chat-id": body.chat_id } : {}),
        ...(body.project_id ? { "x-zwork-project-id": body.project_id } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!resp.ok || !resp.body) {
      const text = await resp.text().catch(() => "");
      onEvent({ type: "error", text: `${resp.status}: ${text}` });
      return;
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      // SSE frames are separated by blank lines
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        parseFrame(frame);
      }
    }
    if (buf.trim()) {
      parseFrame(buf);
    }
    if (!sawTerminal && !sawServerError) {
      throw new Error("Stream closed before terminal event.");
    }
  };

  // Re-attach to the live run's event bus after a connection drop: the
  // durable run keeps going server-side, so instead of re-POSTing the
  // message (a duplicate run, billed twice) we resume the SAME run's event
  // stream from the last bus seq we saw. Returns true when a live run was
  // attached (even if that attachment also dropped — caller re-loops);
  // false when there is no live run to attach to.
  const attachLive = async (): Promise<boolean> => {
    if (!liveChatId) return false;
    const sidecarToken = await getSidecarToken();
    // Attach from the last seq we saw; with none yet (dropped before the
    // first bus event), from 0 so nothing is missed. The replay can include
    // the chat's history and the prompt's own entry; the sidecar only sends
    // queued messages as user_message, so neither comes back as a bubble.
    const url = u(`/api/chats/${liveChatId}/run/live`) + `?after=${lastSeq ?? 0}`;
    let resp: Response;
    try {
      resp = await fetch(url, {
        headers: {
          ...clientHeaders(),
          ...(sidecarToken ? { "x-zwork-token": sidecarToken } : {}),
        },
      });
    } catch {
      return false;
    }
    if (!resp.ok || !resp.body) return false;
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let headerSeen = false;
    const consume = (frame: string) => {
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (!data) continue;
        let evt: any;
        try {
          evt = JSON.parse(data);
        } catch {
          continue;
        }
        if (evt?.type === "run_state") {
          headerSeen = true;
          if (!evt.live) {
            // No live run (finished while we were disconnected) — cancel
            // the read; the caller falls back to its normal retry path.
            void reader.cancel().catch(() => {});
            return;
          }
          return;
        }
        noteEvent(evt);
        onEvent(evt as StreamEvent);
      }
    };
    while (true) {
      const { done, value } = await reader.read().catch(() => ({ done: true, value: undefined }));
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        consume(frame);
      }
    }
    if (buf.trim()) {
      consume(buf);
    }
    return headerSeen;
  };

  while (true) {
    try {
      if (IS_TAURI) {
        await api.waitForBackend(attemptedRecovery ? 30 : 12).catch(() => {});
      }
      await readStream();
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      if (sawTerminal || sawServerError) {
        return;
      }
      if (IS_TAURI && !sawEvent && !attemptedRecovery) {
        attemptedRecovery = true;
        onEvent({ type: "status", text: "Restarting local backend" });
        await invokeBackendCommand("restart_backend");
        await api.waitForBackend(30).catch(() => {});
        continue;
      }

      // Prefer re-attaching to the still-running durable run over
      // re-sending the message.
      if (await attachLive()) {
        if (sawTerminal || sawServerError) {
          return;
        }
        // The attachment itself dropped without a terminal event — try
        // again after a beat (never hot-loop a server accepting then
        // dropping connections).
        await new Promise((resolve) => setTimeout(resolve, 1000));
        continue;
      }

      if (retryCount >= 5) {
        const detail =
          error instanceof Error && error.message
            ? error.message
            : String(error || "unknown error");
        onEvent({
          type: "error",
          text: `Lost connection to the local backend. ${detail}`,
        });
        onEvent({ type: "end" });
        return;
      }

      retryCount++;
      onEvent({
        type: "status",
        text: `Lost connection. Reconnecting (attempt ${retryCount}/5)...`,
      });
      await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * retryCount, 5000)));
      continue;
    }
  }
}
